import type { Abi, Address } from 'viem';
import { GRID_MANAGER_ABI, ERC20_APPROVE_ABI } from './abi';
import type { AbiCall } from './types';

const gridAbi = GRID_MANAGER_ABI as unknown as Abi;
const approveAbi = ERC20_APPROVE_ABI as unknown as Abi;

export function buildSetPriceCall(params: {
  gridManagerAddress: Address;
  gridId: bigint;
  seatIndex: number;
  newPrice: bigint;
}): AbiCall {
  return {
    to: params.gridManagerAddress,
    abi: gridAbi,
    functionName: 'setPrice',
    args: [params.gridId, BigInt(params.seatIndex), params.newPrice],
  };
}

export function buildWithdrawDepositCall(params: {
  gridManagerAddress: Address;
  gridId: bigint;
  seatIndex: number;
  amount: bigint;
}): AbiCall {
  return {
    to: params.gridManagerAddress,
    abi: gridAbi,
    functionName: 'withdrawDeposit',
    args: [params.gridId, BigInt(params.seatIndex), params.amount],
  };
}

export function buildAbandonCall(params: {
  gridManagerAddress: Address;
  gridId: bigint;
  seatIndex: number;
}): AbiCall {
  return {
    to: params.gridManagerAddress,
    abi: gridAbi,
    functionName: 'abandonSeat',
    args: [params.gridId, BigInt(params.seatIndex)],
  };
}

export function buildAddDepositCalls(params: {
  gridManagerAddress: Address;
  gridId: bigint;
  seatIndex: number;
  amount: bigint;
  usdcAddress: Address;
  /** Default 10 — matches in-app approval headroom. */
  approvalBufferMultiplier?: number;
}): { approveCall: AbiCall; depositCall: AbiCall } {
  const mult = BigInt(params.approvalBufferMultiplier ?? 10);
  return {
    approveCall: {
      to: params.usdcAddress,
      abi: approveAbi,
      functionName: 'approve',
      args: [params.gridManagerAddress, params.amount * mult],
    },
    depositCall: {
      to: params.gridManagerAddress,
      abi: gridAbi,
      functionName: 'addDeposit',
      args: [params.gridId, BigInt(params.seatIndex), params.amount],
    },
  };
}

/** Claim empty seats via `addBatch` + ERC20 approve for deposits. */
export function buildClaimSeatCalls(params: {
  gridManagerAddress: Address;
  gridId: bigint;
  seatIndices: number[];
  pricePerSeat: bigint;
  depositPerSeat: bigint;
  usdcAddress: Address;
  approvalBufferMultiplier: number;
}): { approveCall: AbiCall; claimCall: AbiCall } {
  const mult = BigInt(params.approvalBufferMultiplier);
  const totalDeposit = params.depositPerSeat * BigInt(params.seatIndices.length);
  const acquireParams = params.seatIndices.map((seatIndex) => ({
    seatId: BigInt(seatIndex),
    price: params.pricePerSeat,
    maxPrice: 0n,
    amount: params.depositPerSeat,
  }));
  return {
    approveCall: {
      to: params.usdcAddress,
      abi: approveAbi,
      functionName: 'approve',
      args: [params.gridManagerAddress, totalDeposit * mult],
    },
    claimCall: {
      to: params.gridManagerAddress,
      abi: gridAbi,
      functionName: 'addBatch',
      args: [params.gridId, acquireParams],
    },
  };
}

/** Buy out an occupied seat via `addBatch` + ERC20 approve for payment + deposit. */
export function buildBuyoutCalls(params: {
  gridManagerAddress: Address;
  gridId: bigint;
  seatIndex: number;
  newPrice: bigint;
  deposit: bigint;
  currentPrice: bigint;
  usdcAddress: Address;
  maxPriceSlippageBps: number;
  approvalBufferMultiplier: number;
}): { approveCall: AbiCall; buyoutCall: AbiCall } {
  const payment = params.currentPrice + params.deposit;
  const maxPrice =
    params.currentPrice +
    (params.currentPrice * BigInt(params.maxPriceSlippageBps)) / 10000n;
  const mult = BigInt(params.approvalBufferMultiplier);
  return {
    approveCall: {
      to: params.usdcAddress,
      abi: approveAbi,
      functionName: 'approve',
      args: [params.gridManagerAddress, payment * mult],
    },
    buyoutCall: {
      to: params.gridManagerAddress,
      abi: gridAbi,
      functionName: 'addBatch',
      args: [
        params.gridId,
        [
          {
            seatId: BigInt(params.seatIndex),
            price: params.newPrice,
            maxPrice,
            amount: payment,
          },
        ],
      ],
    },
  };
}
